import { useState } from 'react';
import { useIntegrations, useM365Contacts } from '../o365/hooks';
import type { M365Contact } from '../o365/api';
import { contactsApi } from '../crm/api';
import { ContactFormModal } from '../crm/contacts/ContactFormModal';
import type { ContactPrefillData } from '../crm/contacts/ContactFormModal';
import type { Contact } from '../crm/types';
import styles from './DashboardContactsTab.module.css';

// ── Hilfsfunktionen ────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (
      (parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '')
    ).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

const AVATAR_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e',
  '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#3b82f6', '#0ea5e9',
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// ── Kontakt-Kachel ─────────────────────────────────────────────────────────────

function ContactCard({
  contact,
  onClick,
}: {
  contact: M365Contact;
  onClick: () => void;
}) {
  const initials = getInitials(contact.displayName);
  const color    = getAvatarColor(contact.displayName);
  const email    = contact.emailAddresses[0]?.address ?? null;
  const phone    = contact.mobilePhone ?? contact.businessPhones[0] ?? null;

  return (
    <div
      className={styles.card}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
    >
      <div className={styles.cardAvatar} style={{ background: color }}>
        {initials}
      </div>
      <div className={styles.cardName}>{contact.displayName}</div>
      {contact.jobTitle   && <div className={styles.cardJobTitle}>{contact.jobTitle}</div>}
      {contact.companyName && <div className={styles.cardCompany}>{contact.companyName}</div>}
      {email && <div className={styles.cardEmail}>{email}</div>}
      {phone && <div className={styles.cardPhone}>{phone}</div>}
    </div>
  );
}

// ── Kontakt-Zeile ──────────────────────────────────────────────────────────────

function ContactRow({
  contact,
  onClick,
}: {
  contact: M365Contact;
  onClick: () => void;
}) {
  const initials = getInitials(contact.displayName);
  const color    = getAvatarColor(contact.displayName);
  const email    = contact.emailAddresses[0]?.address ?? null;
  const phone    = contact.mobilePhone ?? contact.businessPhones[0] ?? null;

  return (
    <div
      className={styles.row}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
    >
      <div className={styles.rowAvatar} style={{ background: color }}>
        {initials}
      </div>
      <div className={styles.rowName}>{contact.displayName}</div>
      <div className={styles.rowMeta}>{contact.companyName ?? '—'}</div>
      <div className={styles.rowMeta}>{contact.jobTitle ?? '—'}</div>
      <div className={styles.rowMeta}>{email ?? '—'}</div>
      <div className={styles.rowMeta}>{phone ?? '—'}</div>
    </div>
  );
}

// ── Detail-Modal ───────────────────────────────────────────────────────────────

function ContactModal({
  contact,
  onClose,
  onImportToCrm,
  isImporting,
}: {
  contact: M365Contact;
  onClose: () => void;
  onImportToCrm: (contact: M365Contact) => void;
  isImporting?: boolean;
}) {
  const initials = getInitials(contact.displayName);
  const color    = getAvatarColor(contact.displayName);
  const email    = contact.emailAddresses?.[0]?.address ?? null;

  return (
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.modal}>
        {/* Close button */}
        <button className={styles.modalClose} onClick={onClose} title="Schließen"
          style={{ position: 'absolute', top: '1rem', right: '1rem' }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M1 1l10 10M11 1L1 11" />
          </svg>
        </button>

        <div className={styles.modalHeader}>
          <div className={styles.modalAvatar} style={{ background: color }}>
            {initials}
          </div>
          <div>
            <h2 className={styles.modalName}>{contact.displayName}</h2>
            {contact.jobTitle    && <div className={styles.modalJobTitle}>{contact.jobTitle}</div>}
            {contact.companyName && <div className={styles.modalCompany}>{contact.companyName}</div>}
          </div>
        </div>

        <div className={styles.modalDetails}>
          {(contact.emailAddresses ?? []).map((ea, i) =>
            ea.address ? (
              <div key={i} className={styles.detailRow}>
                <span className={styles.detailLabel}>
                  {i === 0 ? 'E-Mail' : `E-Mail ${i + 1}`}
                </span>
                <a href={`mailto:${ea.address}`} className={styles.detailValue}>
                  {ea.address}
                </a>
              </div>
            ) : null,
          )}
          {contact.mobilePhone && (
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Mobil</span>
              <a href={`tel:${contact.mobilePhone}`} className={styles.detailValue}>
                {contact.mobilePhone}
              </a>
            </div>
          )}
          {(contact.businessPhones ?? []).map((p, i) => (
            <div key={i} className={styles.detailRow}>
              <span className={styles.detailLabel}>
                {(contact.businessPhones ?? []).length > 1 ? `Telefon ${i + 1}` : 'Telefon'}
              </span>
              <a href={`tel:${p}`} className={styles.detailValue}>
                {p}
              </a>
            </div>
          ))}
          {!email &&
            !contact.mobilePhone &&
            (contact.businessPhones ?? []).length === 0 && (
              <p className={styles.detailEmpty}>Keine Kontaktdaten hinterlegt.</p>
            )}
        </div>

        {/* CRM Import Button */}
        <div style={{
          borderTop: '1px solid var(--color-border)',
          paddingTop: '1rem',
          marginTop: '0.5rem',
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={() => onImportToCrm(contact)}
            disabled={isImporting}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: 'var(--color-primary)', color: 'white',
              border: 'none', borderRadius: 'var(--radius-sm)',
              fontSize: '0.8125rem', fontWeight: 600,
              cursor: isImporting ? 'wait' : 'pointer',
              opacity: isImporting ? 0.7 : 1,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="12" height="9" rx="1.5" />
              <path d="M4 3V1.5A.5.5 0 014.5 1h5a.5.5 0 01.5.5V3" />
              <path d="M7 6v4M5 8h4" />
            </svg>
            {isImporting ? 'Prüfe Duplikate...' : 'In CRM übernehmen'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Hauptkomponente ────────────────────────────────────────────────────────────

export function DashboardContactsTab() {
  const { data: integrationsData } = useIntegrations();
  const { data, isLoading, isError } = useM365Contacts();
  const [search,   setSearch]   = useState('');
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');
  const [selected, setSelected] = useState<M365Contact | null>(null);
  const [crmImportData, setCrmImportData] = useState<ContactPrefillData | null>(null);
  const [isCrmFormOpen, setIsCrmFormOpen] = useState(false);
  const [existingContact, setExistingContact] = useState<Contact | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const handleImportToCrm = async (contact: M365Contact) => {
    const email = contact.emailAddresses?.[0]?.address ?? '';

    // Parse displayName into first/last name
    const nameParts = contact.displayName.trim().split(/\s+/);
    const firstName = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : nameParts[0] ?? '';
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

    const prefill: ContactPrefillData = {
      firstName,
      lastName,
      email,
      phone: contact.businessPhones?.[0] ?? '',
      mobile: contact.mobilePhone ?? '',
      position: contact.jobTitle ?? '',
      companyName: contact.companyName ?? '',
    };

    // Prüfe ob Kontakt mit dieser E-Mail bereits im CRM existiert
    if (email) {
      setIsImporting(true);
      try {
        const lookupRes = await contactsApi.lookupByEmail(email);
        if (lookupRes?.data?.id) {
          // Kontakt existiert — vollständige Daten laden
          const fullRes = await contactsApi.getById(lookupRes.data.id);
          if (fullRes?.data) {
            // Outlook-Daten in bestehenden Kontakt mergen (leere Felder auffüllen)
            const merged: Contact = {
              ...fullRes.data,
              firstName: fullRes.data.firstName || firstName,
              lastName: fullRes.data.lastName || lastName,
              phone: fullRes.data.phone || prefill.phone || null,
              mobile: fullRes.data.mobile || prefill.mobile || null,
              position: fullRes.data.position || prefill.position || null,
            };
            setExistingContact(merged);
            setCrmImportData(null);
            setSelected(null);
            setIsCrmFormOpen(true);
            setIsImporting(false);
            return;
          }
        }
      } catch {
        // 404 = Kontakt existiert nicht → normal weitermachen
      }
      setIsImporting(false);
    }

    // Kein Duplikat gefunden → neuen Kontakt anlegen
    setExistingContact(null);
    setCrmImportData(prefill);
    setSelected(null);
    setIsCrmFormOpen(true);
  };

  const isConnected =
    integrationsData?.data?.some(
      (i) => i.provider === 'MICROSOFT_365' && i.connected,
    ) ?? false;

  /* ── Nicht verbunden ── */
  if (!isConnected) {
    return (
      <div className={styles.state}>
        <div className={styles.stateIcon}>📇</div>
        <p className={styles.stateTitle}>Microsoft 365 nicht verbunden</p>
        <p className={styles.stateSub}>
          Verbinde dein Microsoft-365-Konto im Profil, um deine Kontakte zu sehen.
        </p>
      </div>
    );
  }

  /* ── Laden ── */
  if (isLoading) {
    return <div className={styles.stateCenter}>Kontakte werden geladen…</div>;
  }

  /* ── Fehler ── */
  if (isError || !data) {
    return (
      <div className={styles.stateCenter} style={{ color: 'var(--color-error, #ef4444)' }}>
        Kontakte konnten nicht geladen werden.
      </div>
    );
  }

  const contacts = data.data ?? [];
  const q        = search.trim().toLowerCase();
  const filtered = q
    ? contacts.filter(
        (c) =>
          c.displayName.toLowerCase().includes(q) ||
          (c.companyName ?? '').toLowerCase().includes(q) ||
          c.emailAddresses.some((ea) => (ea.address ?? '').toLowerCase().includes(q)),
      )
    : contacts;

  return (
    <div className={styles.root}>
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <h1 className={styles.pageHeaderTitle}>Kontakte</h1>
          {contacts.length > 0 && (
            <span className={styles.pageHeaderCount}>{contacts.length} gesamt</span>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        {/* Suche */}
        <div className={styles.searchWrap}>
          <svg
            className={styles.searchIcon}
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          >
            <circle cx="6" cy="6" r="4" />
            <path d="M9 9l3 3" />
          </svg>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Name, E-Mail oder Firma suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              className={styles.searchClear}
              onClick={() => setSearch('')}
              title="Suche leeren"
            >
              ×
            </button>
          )}
        </div>

        {/* Ansicht-Toggle */}
        <div className={styles.viewToggle}>
          <button
            className={`${styles.viewBtn} ${viewMode === 'cards' ? styles.viewBtnActive : ''}`}
            onClick={() => setViewMode('cards')}
            title="Kachelansicht"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
              <rect x="0" y="0" width="6" height="6" rx="1" />
              <rect x="8" y="0" width="6" height="6" rx="1" />
              <rect x="0" y="8" width="6" height="6" rx="1" />
              <rect x="8" y="8" width="6" height="6" rx="1" />
            </svg>
          </button>
          <button
            className={`${styles.viewBtn} ${viewMode === 'list' ? styles.viewBtnActive : ''}`}
            onClick={() => setViewMode('list')}
            title="Listenansicht"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <path d="M1 3h12M1 7h12M1 11h12" />
            </svg>
          </button>
        </div>

        <span className={styles.count}>{filtered.length} Kontakte</span>
      </div>

      {/* Listenansicht-Header */}
      {viewMode === 'list' && (
        <div className={styles.listHeader}>
          <div /> {/* Avatar-Platz */}
          <div className={styles.listHeaderCell}>Name</div>
          <div className={styles.listHeaderCell}>Firma</div>
          <div className={styles.listHeaderCell}>Jobtitel</div>
          <div className={styles.listHeaderCell}>E-Mail</div>
          <div className={styles.listHeaderCell}>Telefon</div>
        </div>
      )}

      {/* Kacheln oder Liste */}
      <div className={viewMode === 'cards' ? styles.grid : styles.list}>
        {filtered.map((c) =>
          viewMode === 'cards' ? (
            <ContactCard key={c.id} contact={c} onClick={() => setSelected(c)} />
          ) : (
            <ContactRow key={c.id} contact={c} onClick={() => setSelected(c)} />
          ),
        )}
      </div>

      {/* Keine Ergebnisse */}
      {filtered.length === 0 && (
        <div className={styles.empty}>Keine Kontakte gefunden.</div>
      )}

      {/* Detail-Modal */}
      {selected && (
        <ContactModal
          contact={selected}
          onClose={() => setSelected(null)}
          onImportToCrm={handleImportToCrm}
          isImporting={isImporting}
        />
      )}

      {/* CRM Kontakt-Formular mit Vorbelegung (Edit bei Duplikat, Create bei neu) */}
      <ContactFormModal
        isOpen={isCrmFormOpen}
        onClose={() => { setIsCrmFormOpen(false); setCrmImportData(null); setExistingContact(null); }}
        onSuccess={() => { setIsCrmFormOpen(false); setCrmImportData(null); setExistingContact(null); }}
        contact={existingContact}
        prefill={existingContact ? undefined : crmImportData}
      />
    </div>
  );
}
